import styled from "styled-components"
import { theme } from "../../theme"
import * as React from "react"
import { HorizontalSpacer, Spacer } from "../../atoms/spacers"
import PageDot from "./PageDot"

type DirectionProps = {
  direction: "left" | "right"
}

type ButtonFilledProps = (GradientProps & DirectionProps) | ButtonOutlinedProps

type ButtonOutlinedProps = { fill: string } & DirectionProps

type GradientProps = { gradient: keyof typeof GRADIENTS }

const GRADIENTS: Record<keyof typeof theme.gradients, JSX.Element> = {
  red: (
    <linearGradient id="gradient" x1="25" y1="0" x2="25" y2="50" gradientUnits="userSpaceOnUse">
      <stop stopColor="#C67C66" />
      <stop offset="1" stopColor="#EAAD81" />
    </linearGradient>
  ),
  blue: (
    <linearGradient id="gradient" x1="25" y1="0" x2="25" y2="50" gradientUnits="userSpaceOnUse">
      <stop stopColor="#81A6C8" />
      <stop offset="1" stopColor="#97D1D9" />
    </linearGradient>
  ),
  green: (
    <linearGradient id="gradient" x1="25" y1="0" x2="25" y2="50" gradientUnits="userSpaceOnUse">
      <stop stopColor="#80B67D" />
      <stop offset="1" stopColor="#AED37C" />
    </linearGradient>
  ),
  yellow: (
    <linearGradient id="gradient" x1="25" y1="0" x2="25" y2="50" gradientUnits="userSpaceOnUse">
      <stop stopColor="#EABB90" />
      <stop offset="1" stopColor="#F3EB73" />
    </linearGradient>
  ),
}

const Gradient: React.FC<GradientProps> = ({ gradient }) => GRADIENTS[gradient]

export const ButtonFilled: React.FC<ButtonFilledProps> = ({ direction, ...props }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="51" height="51" fill="none" viewBox="0 0 51 51">
    <g clipPath="url(#clip0_17_12)">
      {direction === "left" ? (
        <path
          fill={"gradient" in props ? "url(#gradient)" : props.fill}
          fillRule="evenodd"
          clipRule="evenodd"
          d="M25.5.5C11.693.5.5 11.693.5 25.5s11.193 25 25 25 25-11.193 25-25-11.193-25-25-25zm-7.222 25.573l10.018-10.017 1.778 1.778-8.24 8.24 8.24 8.24-1.778 1.777-10.018-10.018z"
        />
      ) : (
        <path
          fill={"gradient" in props ? "url(#gradient)" : props.fill}
          fillRule="evenodd"
          clipRule="evenodd"
          d="M25 0C11.1929 0 0 11.1929 0 25C-2.83618e-06 38.8071 11.1929 50 25 50C38.8071 50 50 38.8071 50 25C50 11.1929 38.8071 2.83618e-06 25 0ZM31.7961 25.5735L21.7782 15.5556L20 17.3338L28.2398 25.5735L20 33.8133L21.7782 35.5914L31.7961 25.5735Z"
        />
      )}
    </g>
    {"gradient" in props && <Gradient gradient={props.gradient} />}
  </svg>
)

const ButtonOutlined: React.FC<ButtonOutlinedProps> = ({ fill, direction }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" fill="none" viewBox="0 0 50 50">
    <g clipPath="url(#clip0_15_8)">
      <circle cx="25" cy="25" r="23.333" stroke={fill} strokeWidth="3.333"></circle>
      {direction === "left" ? (
        <path fill={fill} d="M27.796 15.556L17.778 25.574 27.796 35.59l1.778-1.778-8.24-8.24 8.24-8.24-1.778-1.777z" />
      ) : (
        <path
          fill={fill}
          d="M21.7782 15.5556L31.7961 25.5735L21.7782 35.5914L20 33.8132L28.2398 25.5735L20 17.3338L21.7782 15.5556Z"
        />
      )}
    </g>
    <defs>
      <clipPath id="clip0_15_8">
        <path fill={fill} d="M0 0H50V50H0z"></path>
      </clipPath>
    </defs>
  </svg>
)

interface Props {
  direction: "left" | "right"
  currentIndex: number
  onGoToAnotherIndex: (index: number) => void
  onClickArrowButton: () => void
}

const ButtonContainer = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`

export const CaseCarouselButton: React.FC<Props> = ({
  direction,
  currentIndex,
  onGoToAnotherIndex,
  onClickArrowButton,
}) => {
  const [hovered, setHovered] = React.useState(false)

  const getRandomGradient = (): "red" | "yellow" | "blue" | "green" => {
    const array: Array<"red" | "blue" | "yellow" | "green"> = ["green", "blue", "yellow", "red"]
    const random = array[Math.floor(Math.random() * array.length)]

    return random
  }

  return (
    <ButtonContainer onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <div onClick={onClickArrowButton}>
        {hovered ? (
          <ButtonFilled direction={direction} gradient={getRandomGradient()} />
        ) : (
          <ButtonOutlined direction={direction} fill={theme.colors.white} />
        )}
      </div>
      <HorizontalSpacer spacing={1} />
      <div style={{ display: "flex" }}>
        <PageDot isSelected={currentIndex === 0} onClick={() => onGoToAnotherIndex(0)} />
        <Spacer spacing={0.5} />
        <PageDot isSelected={currentIndex === 1} onClick={() => onGoToAnotherIndex(1)} />
        <Spacer spacing={0.5} />
        <PageDot isSelected={currentIndex === 2} onClick={() => onGoToAnotherIndex(2)} />
      </div>
    </ButtonContainer>
  )
}
